// src/features/tasks/pages/TaskCategoriesPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  Tooltip,
  Card,
  CardContent,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Category as CategoryIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LocalizedDateDistance from '../../../i18n/LocalizedDateDistance';

// Components
import TaskCategoryForm from '../components/TaskCategoryForm';

// Hooks
import { useTaskCategories } from '../hooks/useTaskCategories';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../hooks/mui/useConfirmDialog';
import { usePermissions } from '../../auth/hooks/usePermissions';

import PageHeader from '../../../components/common/PageHeader';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';

export default function TaskCategoriesPage() {
  const { t } = useTranslation(["common", "tasks"]);
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const permissions = usePermissions();

  // State
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color_code: '',
  });
  const [validationErrors, setValidationErrors] = useState({});

  // API hooks
  const {
    categories,
    loading,
    error,
    clearError,
    fetchTaskCategories,
    createTaskCategory,
    updateTaskCategory,
    deleteTaskCategory,
    activateTaskCategory,
    deactivateTaskCategory,
  } = useTaskCategories();

  // Load initial data
  useEffect(() => {
    fetchTaskCategories({ active_only: !includeInactive });
  }, [fetchTaskCategories, includeInactive]);

  // Event handlers
  const handleRefresh = useCallback(() => {
    fetchTaskCategories({ active_only: !includeInactive });
  }, [fetchTaskCategories, includeInactive]);

  const handleIncludeInactiveChange = useCallback((event) => {
    setIncludeInactive(event.target.checked);
  }, []);

  const handleMenuOpen = useCallback((event, category) => {
    event.stopPropagation();
    setSelectedCategory(category);
    setActionMenuAnchor(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setActionMenuAnchor(null);
    setSelectedCategory(null);
  }, []);

  const handleCreateClick = useCallback(() => {
    setFormData({ name: '', description: '', color_code: '' });
    setValidationErrors({});
    setCreateDialogOpen(true);
  }, []);

  const handleEditClick = useCallback((category) => {
    setFormData({
      name: category.name || '',
      description: category.description || '',
      color_code: category.color_code || '',
    });
    setSelectedCategory(category);
    setValidationErrors({});
    setEditDialogOpen(true);
    handleMenuClose();
  }, [handleMenuClose]);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setValidationErrors(prev => {
      if (prev[field]) {
        const { [field]: removed, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const validateForm = useCallback(() => {
    const errors = {};

    if (!formData.name?.trim()) {
      errors.name = t('tasks:categories.validation.name_required');
    } else if (formData.name.length > 255) {
      errors.name = t('tasks:categories.validation.name_max_length');
    }

    if (formData.description && formData.description.length > 1000) {
      errors.description = t('tasks:categories.validation.description_max_length');
    }

    if (formData.color_code && !/^#[0-9A-Fa-f]{6}$/.test(formData.color_code)) {
      errors.color_code = t('tasks:categories.validation.color_invalid');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, t]);

  const handleCreateSubmit = useCallback(async () => {
    if (!validateForm()) return;

    try {
      const result = await createTaskCategory(formData);
      if (result.success) {
        showSuccess(t('tasks:categories.success.created'));
        setCreateDialogOpen(false);
        setFormData({ name: '', description: '', color_code: '' });
      } else {
        showError(result.error || t('tasks:categories.errors.create_failed'));
      }
    } catch (error) {
      showError(t('tasks:categories.errors.create_failed'));
    }
  }, [validateForm, formData, createTaskCategory, showSuccess, showError, t]);

  const handleEditSubmit = useCallback(async () => {
    if (!validateForm() || !selectedCategory) return;

    try {
      const result = await updateTaskCategory(selectedCategory.category_id, formData);
      if (result.success) {
        showSuccess(t('tasks:categories.success.updated'));
        setEditDialogOpen(false);
        setSelectedCategory(null);
      } else {
        showError(result.error || t('tasks:categories.errors.update_failed'));
      }
    } catch (error) {
      showError(t('tasks:categories.errors.update_failed'));
    }
  }, [validateForm, selectedCategory, formData, updateTaskCategory, showSuccess, showError, t]);

  const handleToggleStatus = useCallback(async (category) => {
    try {
      const result = category.is_active 
        ? await deactivateTaskCategory(category.category_id)
        : await activateTaskCategory(category.category_id);

      if (result.success) {
        showSuccess(
          category.is_active 
            ? t('tasks:categories.success.deactivated')
            : t('tasks:categories.success.activated')
        );
      } else {
        showError(
          result.error || (category.is_active 
            ? t('tasks:categories.errors.deactivate_failed')
            : t('tasks:categories.errors.activate_failed'))
        );
      }
    } catch (error) {
      showError(
        category.is_active 
          ? t('tasks:categories.errors.deactivate_failed')
          : t('tasks:categories.errors.activate_failed')
      );
    }
    handleMenuClose();
  }, [activateTaskCategory, deactivateTaskCategory, showSuccess, showError, handleMenuClose, t]);

  const handleDeleteCategory = useCallback((category) => {
    showConfirmDialog({
      title: t('tasks:categories.confirm.delete_title'),
      message: t('tasks:categories.confirm.delete_message', { name: category.name }),
      confirmText: t('tasks:categories.confirm.delete_button'),
      severity: 'error',
      onConfirm: async () => {
        try {
          const result = await deleteTaskCategory(category.category_id);
          if (result.success) {
            showSuccess(t('tasks:categories.success.deleted'));
          } else {
            showError(result.error || t('tasks:categories.errors.delete_failed'));
          }
        } catch (error) {
          showError(t('tasks:categories.errors.delete_failed'));
        }
      },
    });
    handleMenuClose();
  }, [showConfirmDialog, deleteTaskCategory, showSuccess, showError, handleMenuClose, t]);

  // Check permissions
  const canCreate = permissions.hasScope('task-category:create');
  const canUpdate = permissions.hasScope('task-category:update');
  const canDelete = permissions.hasScope('task-category:delete');
  const canActivate = permissions.hasScope('task-category:activate');
  const canDeactivate = permissions.hasScope('task-category:deactivate');
  // The ⋮ menu only for someone who can do something in it
  const hasCategoryActions = canUpdate || canDelete || canActivate || canDeactivate;

  return (
    <Box>
      {/* Header */}
      <PageHeader
        icon={LabelOutlinedIcon}
        title={t('tasks:categories.title')}
        subtitle={t('tasks:categories.subtitle')}
        actions={<>
          <FormControlLabel
            control={
              <Switch
                checked={includeInactive}
                onChange={handleIncludeInactiveChange}
                size="small"
              />
            }
            label={t('tasks:categories.include_inactive')}
          />

          <Tooltip title={t('tasks:categories.actions.refresh')}>
            <span>
              <IconButton onClick={handleRefresh} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>

          {canCreate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateClick}
              disabled={loading}
            >
              {t('tasks:categories.create_category')}
            </Button>
          )}
        </>}
      />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* Categories Grid */}
      {loading ? (
        <Box display="flex" alignItems="center" justifyContent="center" py={8}>
          <Typography variant="body1" color="text.secondary">
            {t('tasks:categories.messages.loading')}
          </Typography>
        </Box>
      ) : categories.length === 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={8}
        >
          <CategoryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('tasks:categories.messages.no_categories')}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" paragraph>
            {includeInactive 
              ? t('tasks:categories.messages.no_categories_created')
              : t('tasks:categories.messages.no_active_categories')
            }
          </Typography>
          {canCreate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateClick}
            >
              {t('tasks:categories.create_first_category')}
            </Button>
          )}
        </Box>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="repeat(auto-fill, minmax(300px, 1fr))"
          gap={3}
        >
          {categories.map((category) => (
            <Card 
              key={category.category_id}
              sx={{ 
                height: '100%',
                cursor: 'pointer',
                '&:hover': { 
                  boxShadow: 4,
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.2s ease-in-out',
                opacity: category.is_active ? 1 : 0.7,
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1}>
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      {category.color_code && (
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            backgroundColor: category.color_code,
                          }}
                        />
                      )}
                      <Typography variant="h6" fontWeight="bold">
                        {category.name}
                      </Typography>
                    </Box>
                    
                    {/* <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Chip
                        icon={category.is_active ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        label={category.is_active ? t('tasks:categories.status.active') : t('tasks:categories.status.inactive')}
                        size="small"
                        color={category.is_active ? 'success' : 'warning'}
                        variant="outlined"
                      />
                      
                      <Chip
                        icon={<AssignmentIcon />}
                        label={t('tasks:categories.messages.tasks_count', { count: category.task_count || 0 })}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    </Box> */}
                  </Box>
                  
                  {hasCategoryActions && (
                    <IconButton size="small" onClick={(e) => handleMenuOpen(e, category)}>
                      <MoreVertIcon />
                    </IconButton>
                  )}
                </Box>

                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    mb: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    minHeight: '3.6em',
                  }}
                >
                  {category.description || t('tasks:categories.no_description')}
                </Typography>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('tasks:categories.created_time_ago')}
                    {" "}
                    <LocalizedDateDistance date={category.created_at} />
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{ elevation: 3 }}
      >
        {canUpdate && (
          <MenuItem onClick={() => handleEditClick(selectedCategory)}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('tasks:categories.actions.edit')}</ListItemText>
          </MenuItem>
        )}

        {selectedCategory && (selectedCategory.is_active ? canDeactivate : canActivate) && (
          <MenuItem onClick={() => handleToggleStatus(selectedCategory)}>
            <ListItemIcon>
              {selectedCategory.is_active ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>
              {selectedCategory.is_active ? t('tasks:categories.actions.deactivate') : t('tasks:categories.actions.activate')}
            </ListItemText>
          </MenuItem>
        )}

        {canDelete && (
          <MenuItem onClick={() => handleDeleteCategory(selectedCategory)}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>
              <Typography color="error">{t('tasks:categories.actions.delete')}</Typography>
            </ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Create Category Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('tasks:categories.create_category')}</DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <TaskCategoryForm
              formData={formData}
              onInputChange={handleInputChange}
              validationErrors={validationErrors}
              loading={loading}
              mode="create"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>{t('tasks:categories.actions.cancel')}</Button>
          <Button onClick={handleCreateSubmit} variant="contained" disabled={loading}>
            {t('tasks:categories.create_category')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('tasks:categories.edit_category')}</DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <TaskCategoryForm
              formData={formData}
              onInputChange={handleInputChange}
              validationErrors={validationErrors}
              loading={loading}
              mode="edit"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>{t('tasks:categories.actions.cancel')}</Button>
          <Button onClick={handleEditSubmit} variant="contained" disabled={loading}>
            {t('tasks:categories.update_category')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog />
    </Box>
  );
}